import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Check, X, ToggleLeft, ToggleRight } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [demoMode, setDemoMode] = useState("labeled");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [q, d] = await Promise.all([
        api.get("/admin/license-queue"),
        api.get("/admin/demo-mode"),
      ]);
      setQueue(q.data);
      setDemoMode(d.data.demo_mode);
    } catch (e) {
      console.error("Failed to load admin data:", e);
    }
    setLoading(false);
  };
  useEffect(() => { if (user?.is_admin) load(); else setLoading(false); /* eslint-disable-next-line */ }, [user]);

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  const review = async (id, decision) => {
    try {
      await api.post(`/admin/license/${id}/review`, { decision });
      toast.success(`License ${decision}d.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleDemo = async () => {
    const next = demoMode === "hidden" ? "labeled" : "hidden";
    try {
      await api.post("/admin/demo-mode", { mode: next });
      setDemoMode(next);
      toast.success(`Demo data is now ${next === "hidden" ? "hidden" : "labeled"}.`);
    } catch (e) {
      toast.error("Failed to update demo mode");
    }
  };

  return (
    <div data-testid="admin-page" className="max-w-3xl mx-auto space-y-10">
      <div>
        <p className="overline text-[#E07A5F]">Administration</p>
        <h1 className="font-serif-dl text-4xl sm:text-5xl tracking-tight mt-2">Admin</h1>
      </div>

      <section className="bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif-dl text-2xl tracking-tight">Demo data</h2>
            <p className="text-sm text-[#5C5A54] mt-1">
              example.com daps are {demoMode === "hidden" ? "hidden in production mode" : "labeled DEMO"}.
            </p>
          </div>
          <button
            data-testid="demo-mode-toggle"
            onClick={toggleDemo}
            className="flex items-center gap-2 text-[#1B4D3E]"
          >
            {demoMode === "hidden" ? <ToggleRight size={34} /> : <ToggleLeft size={34} className="text-[#A3A095]" />}
            <span className="text-sm font-medium">{demoMode === "hidden" ? "Hidden" : "Labeled"}</span>
          </button>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 text-[#1B4D3E] mb-4">
          <ShieldCheck size={18} />
          <h2 className="font-serif-dl text-2xl tracking-tight">License review queue</h2>
        </div>
        {loading ? (
          <p className="text-[#5C5A54]">Loading…</p>
        ) : queue.length === 0 ? (
          <div className="bg-white border border-dashed border-[#DEDBD3] rounded-lg p-10 text-center text-[#5C5A54]">
            No pending submissions.
          </div>
        ) : (
          <div className="space-y-3">
            {queue.map((s) => (
              <div
                key={s.id}
                data-testid={`license-sub-${s.id}`}
                className="bg-white border border-[#DEDBD3] rounded-lg p-5 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="font-medium">{s.legal_name}</p>
                  <p className="text-sm text-[#5C5A54]">
                    {s.state} · License {s.license_number}
                  </p>
                  <p className="text-xs text-[#8A877F]">{s.email}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`approve-${s.id}`}
                    onClick={() => review(s.id, "approve")}
                    className="px-3 py-2 rounded-md bg-[#E6F0EB] text-[#2E6B4F] border border-[#2E6B4F]/20 hover:bg-[#d9ebe0] flex items-center gap-1 text-sm"
                  >
                    <Check size={15} /> Approve
                  </button>
                  <button
                    data-testid={`reject-${s.id}`}
                    onClick={() => review(s.id, "reject")}
                    className="px-3 py-2 rounded-md bg-[#F5E6E6] text-[#9C3A3A] border border-[#9C3A3A]/20 hover:bg-[#f0dada] flex items-center gap-1 text-sm"
                  >
                    <X size={15} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
